import React, { useEffect, useMemo, useRef } from "react";
import ReactSelect from "react-select";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
];

const STOCK_OPTIONS = [
  { value: "", label: "All" },
  { value: "1", label: "In Stock" },
  { value: "0", label: "Out of Stock" },
];

const B2B_OPTIONS = [
  { value: "", label: "All" },
  { value: "1", label: "B2B Only" },
  { value: "0", label: "Non-B2B" },
];

const FILTER_KEYS = [
  "category_id",
  "sub_category_id",
  "brand_id",
  "occasion_id",
  "from_date",
  "to_date",
  "status",
  "in_stock",
  "is_b2b",
];

const toMultiValue = (idsCsv, options) => {
  if (!idsCsv) return [];
  const ids = idsCsv.split(",");
  return options.filter((o) => ids.includes(String(o.value)));
};

const findOption = (options, value) =>
  options.find((o) => o.value === (value ?? "")) || null;

const addOneDay = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().split("T")[0];
};

const ProductFiltersBar = ({
  params,
  onChange,
  moreOpen,
  onToggleMore,
  categoryOptions,
  subcategoryOptions,
  brandOptions,
  occasionOptions,
}) => {
  const morePanelRef = useRef(null);
  const moreBtnRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => {
      if (
        morePanelRef.current?.contains(e.target) ||
        moreBtnRef.current?.contains(e.target)
      )
        return;
      onToggleMore(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreOpen, onToggleMore]);

  const selectedCategories = useMemo(
    () => toMultiValue(params.category_id, categoryOptions),
    [params.category_id, categoryOptions]
  );
  const selectedSubcategories = useMemo(
    () => toMultiValue(params.sub_category_id, subcategoryOptions),
    [params.sub_category_id, subcategoryOptions]
  );
  const selectedBrands = useMemo(
    () => toMultiValue(params.brand_id, brandOptions),
    [params.brand_id, brandOptions]
  );
  const selectedOccasions = useMemo(
    () => toMultiValue(params.occasion_id, occasionOptions),
    [params.occasion_id, occasionOptions]
  );

  const setMulti = (key) => (options) => {
    const csv = (options || []).map((o) => o.value).join(",");
    onChange({ [key]: csv, page: 1 });
  };

  const setSingle = (key) => (opt) => {
    onChange({ [key]: opt?.value || "", page: 1 });
  };

  const reset = () => {
    const cleared = FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});
    onChange({ ...cleared, page: 1 });
  };

  const moreActiveCount = [
    "category_id",
    "sub_category_id",
    "brand_id",
    "occasion_id",
    "is_b2b",
    "from_date",
    "to_date",
  ].reduce((n, k) => (params[k] ? n + 1 : n), 0);

  return (
    <div className="plx-filters">
      <ReactSelect
        className="plx-rs"
        classNamePrefix="plx-rs"
        placeholder="Status"
        value={findOption(STATUS_OPTIONS, params.status)}
        onChange={setSingle("status")}
        options={STATUS_OPTIONS}
        isSearchable={false}
        isClearable
        aria-label="Status"
      />

      <ReactSelect
        className="plx-rs"
        classNamePrefix="plx-rs"
        placeholder="Stock"
        value={findOption(STOCK_OPTIONS, params.in_stock)}
        onChange={setSingle("in_stock")}
        options={STOCK_OPTIONS}
        isSearchable={false}
        isClearable
        aria-label="Stock"
      />

      <div className="plx-filters__more-wrap">
        <button
          ref={moreBtnRef}
          type="button"
          className={`plx-btn-more ${moreOpen ? "is-open" : ""}`}
          onClick={() => onToggleMore(!moreOpen)}
          aria-expanded={moreOpen}
        >
          <i className="fa fa-sliders-h" aria-hidden="true" />
          More filters
          {moreActiveCount > 0 && <span className="plx-badge">{moreActiveCount}</span>}
          <i className={`fa fa-chevron-${moreOpen ? "up" : "down"} ms-1`} aria-hidden="true" />
        </button>

        {moreOpen && (
          <div
            ref={morePanelRef}
            className="plx-more-panel"
            role="dialog"
            aria-label="More filters"
          >
            <div className="plx-more-panel__grid">
              <div>
                <label className="plx-field-label">Category</label>
                <ReactSelect
                  className="plx-rs"
                  classNamePrefix="plx-rs"
                  isMulti
                  value={selectedCategories}
                  onChange={setMulti("category_id")}
                  options={categoryOptions}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="plx-field-label">Sub Category</label>
                <ReactSelect
                  className="plx-rs"
                  classNamePrefix="plx-rs"
                  isMulti
                  value={selectedSubcategories}
                  onChange={setMulti("sub_category_id")}
                  options={subcategoryOptions}
                  isDisabled={!params.category_id}
                  placeholder={params.category_id ? "All" : "Pick a category first"}
                />
              </div>
              <div>
                <label className="plx-field-label">Brand</label>
                <ReactSelect
                  className="plx-rs"
                  classNamePrefix="plx-rs"
                  isMulti
                  value={selectedBrands}
                  onChange={setMulti("brand_id")}
                  options={brandOptions}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="plx-field-label">Occasion</label>
                <ReactSelect
                  className="plx-rs"
                  classNamePrefix="plx-rs"
                  isMulti
                  value={selectedOccasions}
                  onChange={setMulti("occasion_id")}
                  options={occasionOptions}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="plx-field-label">B2B</label>
                <ReactSelect
                  className="plx-rs"
                  classNamePrefix="plx-rs"
                  value={findOption(B2B_OPTIONS, params.is_b2b)}
                  onChange={setSingle("is_b2b")}
                  options={B2B_OPTIONS}
                  isSearchable={false}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="plx-field-label">Created between</label>
                <div className="plx-date-range">
                  <input
                    type="date"
                    value={params.from_date || ""}
                    onChange={(e) => {
                      const next = e.target.value;
                      const clearTo =
                        params.to_date && params.to_date <= next
                          ? ""
                          : params.to_date;
                      onChange({ from_date: next, to_date: clearTo, page: 1 });
                    }}
                    className="plx-date-input"
                    aria-label="From date"
                  />
                  <span className="plx-date-sep">→</span>
                  <input
                    type="date"
                    value={params.to_date || ""}
                    min={addOneDay(params.from_date)}
                    onChange={(e) =>
                      onChange({ to_date: e.target.value, page: 1 })
                    }
                    className="plx-date-input"
                    aria-label="To date"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <button type="button" className="plx-btn-clear" onClick={reset}>
        Clear filter
      </button>
    </div>
  );
};

export const countActiveFilters = (params) =>
  FILTER_KEYS.reduce((n, k) => (params?.[k] ? n + 1 : n), 0);

export default ProductFiltersBar;
